import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { introBridge, introPoll, introReward, peerLabel, references, unmetNeedHeadline } from '../data/teaser';
import { PollBlock } from './PollBlock';
interface QuizOverlayProps {
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const DOTS = Array.from({
  length: 100
}, (_, i) => i);
const HIGHLIGHTED = 46;
export function QuizOverlay({
  onClose
}: QuizOverlayProps) {
  const [answer, setAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const select = (id: string) => {
    setAnswer(id);
    window.setTimeout(() => setShowResult(true), 380);
  };
  const mine = introPoll.options.find(o => o.id === answer);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE
  }} role="dialog" aria-modal="true" aria-label="Quiz" className="absolute inset-0 z-20 flex flex-col bg-paper" data-id="element-70" data-mp="2w4acr8lffn2k">
      <div className="flex items-center justify-between border-b border-line px-5 py-3" data-id="element-71" data-mp="1junctgwzmn1">
        <p className="text-[9px] uppercase tracking-micro text-blue" data-id="element-72" data-mp="fazet8a4p8ma">
          {showResult ? 'Your answer · peer responses' : 'Quiz'}
        </p>
        <button type="button" onClick={onClose} aria-label="Close quiz" className="flex h-7 w-7 items-center justify-center rounded-full text-mut transition-colors duration-150 hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-73" data-mp="ajhcogwdd1zq">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-74" data-mp="y9bocv4yckie" />
        </button>
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto px-5 py-5" data-id="element-75" data-mp="tc22fpgruqzb">
        {!showResult ? <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="flex flex-1 flex-col justify-center" data-id="element-76" data-mp="301zupdcw7o2e">
            <h3 className="font-display text-[24px] font-semibold leading-[1.1] text-navy" data-id="element-77" data-mp="z13al1hbdhn4">
              {unmetNeedHeadline}
            </h3>
            <p className="mt-5 text-[13px] font-medium leading-snug text-navy2" data-id="element-78" data-mp="18wwyfh0hgxk7">
              {introPoll.question}
            </p>
            <div className="mt-4" data-id="element-79" data-mp="1d0be26kifbx4">
              <PollBlock poll={introPoll} selected={answer} onSelect={select} hideSummary data-id="element-80" data-mp="1hxutvawuvwr6" />
            </div>
          </motion.div> : <motion.div initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="flex flex-1 flex-col" data-id="element-81" data-mp="1mvwei09xk44r">
            <p className="text-[9px] uppercase tracking-micro text-mut" data-id="element-82" data-mp="1wly0mmiq3ch1">{peerLabel}</p>
            {mine && <p className="mt-1.5 text-[13px] leading-snug text-navy" data-id="element-83" data-mp="20m9khx39h8rg">
                <span className="font-display text-[20px] font-bold tabular-nums text-blue" data-id="element-84" data-mp="25o76v06ykjom">
                  {mine.share}%
                </span>{' '}
                of your peers answered the same
              </p>}

            <p className="mt-5 text-center font-display text-[52px] font-bold leading-none tracking-tight tabular-nums text-blue" data-id="element-85" data-mp="3l8q187n3v3qc">
              31–46%
            </p>

            <div className="mx-auto mt-5 grid w-full max-w-[220px] grid-cols-10 gap-[5px]" role="img" aria-label="Dot grid of 100 patients, with 31 to 46 shown as reporting incomplete functional improvement" data-id="element-86" data-mp="3c79qfysdozll">
              {DOTS.map(i => <motion.span key={i} initial={{
            backgroundColor: '#DCE8EE'
          }} animate={{
            backgroundColor: i < HIGHLIGHTED ? '#1E76CF' : '#DCE8EE'
          }} transition={{
            duration: 0.2,
            delay: i < HIGHLIGHTED ? i * 0.004 : 0,
            ease: EASE
          }} className="aspect-square w-full rounded-full" data-id="element-87" data-mp="3woye9tpxvhn" />)}
            </div>

            <p className="mt-5 text-[12.5px] leading-relaxed text-navy" data-id="element-88" data-mp="g8zigqyopre5">{introReward}</p>
            <p className="mt-3 font-display text-[17px] leading-snug text-blue" data-id="element-89" data-mp="28ygf86oodx3">{introBridge}</p>

            <p className="mt-auto pt-4 text-[8.5px] leading-snug text-mut" data-id="element-90" data-mp="77j6ies81h7t">{references.abraham}</p>

            <button type="button" onClick={onClose} className="mt-4 flex h-11 w-full items-center justify-center rounded-full bg-navy text-[13px] font-medium text-paper transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-91" data-mp="uxlw8o8nyb39">
              Back to the phenotypes
            </button>
          </motion.div>}
      </div>
    </motion.div>;
}